import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { loginSchema, LoginFormData } from '../utils/validators';
import { useAuth } from '../hooks/useAuth';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import toast from 'react-hot-toast';

export default function Login() {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    setError('');
    try {
      await login(data.username, data.password);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Invalid credentials';
      setError(msg);
      // Shake animation
      cardRef.current?.classList.add('animate-shake');
      setTimeout(() => cardRef.current?.classList.remove('animate-shake'), 500);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-alt flex items-center justify-center px-4">
      <div
        ref={cardRef}
        className="w-full max-w-sm bg-white rounded-card p-8 shadow-xl"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-primary text-2xl font-bold">Plus</span>
            <span className="bg-primary-light text-primary text-[10px] font-semibold px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">Admin Panel</h1>
          <p className="text-xs text-text-hint mt-1">Plus FTP & SFTP Server</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Username"
            placeholder="Enter username"
            {...register('username')}
            error={errors.username?.message}
          />
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter password"
            {...register('password')}
            error={errors.password?.message}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-text-hint hover:text-text-secondary"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />

          {error && (
            <p className="text-sm text-error text-center bg-red-50 py-2 rounded-btn">{error}</p>
          )}

          <Button type="submit" loading={loading} className="w-full h-11">
            Login
          </Button>
        </form>
      </div>
    </div>
  );
}
